import { FormData } from "../types";

import {
  ViewOffSlashIcon,
  ToggleOffIcon,
  Notification01Icon,
  PlazaIcon,
  AlertCircleIcon,
  PrinterIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { ApplicantStatus } from "shared/types";
import { getApplicantRole } from "utils/getApplicantRole";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ActionSuccessCard } from "../components/ActionSuccessCard";
import { useAuthContext } from "providers/Auth";
import { Card } from "shared/components/Card";
import { IconButton, IconButtonSize } from "shared/components/IconButton";

export const SuccessView = ({
  formData,
  address,
  applicants,
  applicationId
}: {
  formData: FormData;
  address: string;
  applicants: Applicant[];
  applicationId?: string | number;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type } = useAuthContext();

  const notifiedApplicants = applicants.filter(
    (a) => a.application_status === ApplicantStatus.Submitted
  );

  const applicantsRequestedReceipt = notifiedApplicants.filter(
    (a) => a.itemized_receipt_consented_at
  );

  return (
    <div className="p-6 bg-background-900 min-h-full space-y-4 px-14">
      {applicantsRequestedReceipt.length > 0 ? (
        <Card className="px-6">
          <div className="py-6 pb-4 border-b border-alpha-40">
            <div className="flex items-center gap-4">
              <HugeiconsIcon
                icon={AlertCircleIcon}
                size={32}
                className="text-primary-500 shrink-0"
              />
              <p className="text-base font-medium text-primary-500">
                The following applicants requested a paper itemized receipt
              </p>
            </div>
          </div>
          <div className="pt-4 pb-6 space-y-4">
            {applicantsRequestedReceipt.map((applicant) => (
              <div
                key={applicant.id}
                className="flex items-center justify-between"
              >
                <p className="text-sm font-medium text-content-1000">
                  {getFullName({
                    firstName: applicant.first_name,
                    lastName: applicant.last_name,
                    middleName: applicant.middle_name,
                    short: true
                  })}
                  ,{" "}
                  <span className="text-content-400">
                    {getApplicantRole(applicant.role)}
                  </span>
                </p>
                <IconButton
                  HugeIcon={PrinterIcon}
                  size={IconButtonSize.SM}
                  onClick={() =>
                    applicationId &&
                    window.open(
                      `/applications/${applicationId}/reports/print/receipt?applicantId=${applicant.id}`,
                      "_blank"
                    )
                  }
                />
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {formData.unlistUnit ? (
        <ActionSuccessCard
          icon={ViewOffSlashIcon}
          title={`${dictionary.Unit} unlisted`}
        >
          <p className="text-sm font-medium text-content-1000">
            {dictionary.Unit} {address} unlisted from the{" "}
            {type.isBoomCRM ? "listing page" : "portfolio page"}
          </p>
        </ActionSuccessCard>
      ) : null}

      {formData.disallowApplications ? (
        <ActionSuccessCard
          icon={ToggleOffIcon}
          title={`Applying disallowed for ${dictionary.unit}`}
        >
          <p className="text-sm font-medium text-content-1000">
            Acceptance of new applications for {dictionary.Unit}{" "}
            {address.split(",")[0]} at {address.split(",").slice(1).join(",")},
            has been paused
          </p>
        </ActionSuccessCard>
      ) : null}

      {formData.sendNotifications ? (
        <ActionSuccessCard
          icon={Notification01Icon}
          title="Email about decision have been sent"
        >
          <div className="space-y-4">
            {notifiedApplicants.map((applicant) => (
              <div
                key={applicant.id}
                className="text-sm font-medium text-content-1000"
              >
                {getFullName({
                  firstName: applicant.first_name,
                  lastName: applicant.last_name,
                  middleName: applicant.middle_name,
                  short: true
                })}
                ,{" "}
                <span className="text-content-400">
                  {getApplicantRole(applicant.role)}
                </span>
              </div>
            ))}
          </div>
        </ActionSuccessCard>
      ) : null}

      {formData.addApplicantsToPMS ? (
        <ActionSuccessCard
          icon={PlazaIcon}
          title="Prospects have been created in your Property Management Software"
          className="px-6 mb-4"
        />
      ) : null}
    </div>
  );
};
